import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './../styles/order-success.css';
import './../styles/modern-category-grid.css';
import { Copy } from "lucide-react";
import ToastNotification, { showToast } from '../components/toasts/ToastNotification';
import {
  FaFacebookMessenger,
  FaRegCheckCircle,
  FaLightbulb,
} from "react-icons/fa";
import { IoReceiptSharp } from "react-icons/io5";
import { useSettings } from '../context/SettingsContext';
import { getProductsByType } from '../api/product.api';
import { ModernCategoryCard } from '../components/category/ModernCategoryGrid';

const TUTORIAL_URL = 'https://drive.google.com/file/d/1in8qyPfqUWg-fBfHERNhjo7xBP_tsAKW/view?fbclid=IwY2xjawKeOp5leHRuA2FlbQIxMQABHjhwab4wieCD1xx4hDLPn22DO2JptGFlBc813MW7fHvrCxbV_QO3P-LgKaWp_aem_62tXuLMMKQ_YLLXVYLI4JA';

const OrderSuccess = () => {
  const { state } = useLocation();
  const {
    orderId,
    orderIds = [],
    isBatch = false,
    product_type,
    order_attributes,
    isValuable
  } = state || {};
  const primaryOrderId = orderId || orderIds?.[0] || null;
  const navigate = useNavigate();
  const settings = useSettings();
  const [recommendations, setRecommendations] = useState([]);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);

  useEffect(() => {
    const pickRandomItems = (list, limit = 4) => {
      const cloned = [...list];
      for (let i = cloned.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [cloned[i], cloned[j]] = [cloned[j], cloned[i]];
      }
      return cloned.slice(0, limit);
    };

    const fetchRecommendations = async () => {
      try {
        setLoadingRecommendations(true);
        const res = await getProductsByType("game_account", 20, 1);
        if (res?.success) {
          const products = Array.isArray(res?.data?.products) ? res.data.products : [];
          setRecommendations(pickRandomItems(products, 4));
        }
      } catch (error) {
        setRecommendations([]);
      } finally {
        setLoadingRecommendations(false);
      }
    };

    fetchRecommendations();
  }, []);

  const copyToClipboard = (text) => {
    if (!text) return;
    showToast("Copy thành công", "success");
    navigator.clipboard.writeText(text);
  };

  const shouldShowCredentials = !isBatch && (product_type === 'gift_card' || (product_type === 'game_account' && !isValuable));
  const shouldShowUtilData = !isBatch && product_type === 'utility_account' && !isValuable;

  const getSuccessMessage = () => {
    if (isBatch) {
      return `Bạn đã thanh toán thành công ${orderIds.length} đơn hàng. Bạn có thể xem chi tiết từng đơn trong mục đơn hàng của tài khoản.`;
    }

    if (product_type === 'game_account') {
      return isValuable
        ? "Bạn vui lòng copy mã đơn hàng và nhắn tin qua Messenger để shop hỗ trợ thay đổi thông tin tài khoản."
        : "Vui lòng thay đổi thông tin Gmail để quản lí tài khoản của bạn, Gmail này sẽ bị xoá sau 3 ngày.";
    }
    if (product_type === 'topup_package') {
      return "Bạn vui lòng copy mã đơn hàng và nhắn tin qua Messenger để shop hỗ trợ nạp game cho mình ạ.";
    }
    if (product_type === 'utility_account' && isValuable) {
      return "Bạn vui lòng copy mã đơn hàng và nhắn tin qua Messenger để shop gửi tài khoản và mật khẩu cho mình ạ.";
    }
    return null;
  };

  const renderActions = () => {
    const actions = [
      {
        label: isBatch ? "Xem đơn hàng của tôi" : "Xem chi tiết đơn hàng",
        icon: <IoReceiptSharp />,
        action: () => navigate(isBatch ? `/order` : `/order/${primaryOrderId}`)
      }
    ];

    // Game Account (not valuable) gets tutorial
    if (product_type === 'game_account' && !isValuable) {
      actions.push({
        label: "Hướng dẫn thay đổi thông tin",
        icon: <FaLightbulb />,
        action: () => window.open(TUTORIAL_URL, "_blank")
      });
    }

    // Valuable or requires support
    if (
      (product_type === 'game_account' && isValuable) ||
      (product_type === 'utility_account' || product_type === 'gift_card') ||
      product_type === 'topup_package'
    ) {
      actions.push({
        label: "Liên hệ qua Messenger",
        icon: <FaFacebookMessenger />,
        action: () => window.open(settings?.contact_messenger_url || "https://www.facebook.com/iampesmobile", "_blank", "noopener,noreferrer")
      });
    }

    return actions.map((btn, idx) => (
      <button key={idx} onClick={btn.action} className="view-order-btn">
        {btn.icon}{btn.label}
      </button>
    ));
  };

  return (
    <div className="order-success-container">
      <ToastNotification />
      <div className="order-success-card">
        <div className="order-success-head">
          <FaRegCheckCircle className='order-success-icon' />
          <div>
            <h2 className="success-title">Đơn Hàng Thành Công</h2>
            <p className='success-subtitle'>Giao dịch đã được ghi nhận thành công.</p>
          </div>
        </div>

        <div className="order-details">
          <div className="order-id-row">
            <strong>{isBatch ? "Mã đơn hàng:" : "Mã đơn hàng:"}</strong>
            <div className='order-id-copy-row'>
              <span className='orderId'>{primaryOrderId || "--"}</span>
              {primaryOrderId && <Copy className="copy-icon" onClick={() => copyToClipboard(primaryOrderId)} />}
            </div>
          </div>

          {isBatch && orderIds.length > 1 && (
            <div className='order-attributes-container'>
              {orderIds.map((id) => (
                <div className="order-success-product-username" key={id}>
                  <strong>{id}</strong>
                  <Copy className="copy-icon" onClick={() => copyToClipboard(id)} />
                </div>
              ))}
            </div>
          )}

          {shouldShowCredentials && (
            <div className='order-attributes-container'>
              <div className="order-success-product-username">
                <strong>Tên đăng nhập:</strong>
                <strong className='order-success-product-attributes-text'>{order_attributes?.username}</strong>
              </div>
              <p className="order-success-product-password">
                <strong>Mật khẩu:</strong>
                <strong className='order-success-product-attributes-text'>{order_attributes?.password}</strong>
              </p>
            </div>
          )}

          {shouldShowUtilData && (
            <div className='order-attributes-container'>
              {Array.isArray(order_attributes) && order_attributes.length > 0 && order_attributes.map((od) => (
                <div className="order-success-product-username" key={od?.label}>
                  <strong>{od.label}:</strong>
                  <strong className='order-success-product-attributes-text'>{od.value}</strong>
                </div>
              ))}
            </div>
          )}
        </div>

        {getSuccessMessage() && (
          <p className="success-message">{getSuccessMessage()}</p>
        )}

        <div className="cta-container">
          {renderActions()}
        </div>

        <p className="thanks-message">
          Cám ơn bạn đã tin tưởng và mua sắm tại Iampesmobile.com!
        </p>
      </div>

      <section className='order-success-recommend-block'>
        <h3 className='order-success-recommend-title'>Sản phẩm gợi ý cho bạn</h3>

        {loadingRecommendations && (
          <p className='order-success-recommend-empty'>Đang tải sản phẩm gợi ý...</p>
        )}

        {!loadingRecommendations && recommendations.length === 0 && (
          <p className='order-success-recommend-empty'>Hiện chưa có sản phẩm gợi ý phù hợp.</p>
        )}

        {!loadingRecommendations && recommendations.length > 0 && (
          <div
            className="order-success-recommend-grid dashboard-product-grid mgc-grid mgc-grid--game"
          >
            {recommendations.map((item) => (
              <ModernCategoryCard
                key={item?._id || item?.productId || item?.product_slug}
                item={item}
                type="game"
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default OrderSuccess;
